import { FC, useMemo, useState } from "react";
import { MiList, MiModalConfirm } from "@miplatform/components";
import {
  BUTTON_LABELS,
  CONFIG_PAGES,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useDebounce, useRouter } from "@miplatform/hooks";
import {
  CalendarEventType,
  CalendarSetStatus,
  ICalendarSet,
  IVerticalTabProps,
  PlatformModules,
} from "@miplatform/types";
import { AddButton, VerticalTabsLayout } from "@mibuildweb/components";
import { useCalendarSets, usePermissions } from "@mibuildweb/hooks";
import { CONFIG_PATHS } from "../../../routes/config/constants";
import { ConfigPageLayout } from "../ConfigPageLayout";
import { ConfigCalendarSetCard } from "./ConfigCalendarSetCard/ConfigCalendarSetCard";
import { CALENDAR_PAGE_CONFIG } from "./ConfigCalendarSetList.constants";
import { ICalendarSetDuplicateOptions } from "./ConfigCalendarSetList.types";
import { ConfigDuplicateCalendarModal } from "./ConfigDuplicateCalendarModal/ConfigDuplicateCalendarModal";

export const ConfigCalendarSetList: FC<ConfigCalendarSetListProps> = ({
  module,
  eventType,
}) => {
  const { navigate, pathname } = useRouter();
  const [selectedCalendarToDelete, setSelectedCalendarToDelete] =
    useState<ICalendarSet | null>(null);
  const [selectedCalendarToDuplicate, setSelectedCalendarToDuplicate] =
    useState<ICalendarSet | null>(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const { hasPermission } = usePermissions();

  const canCreateCalendar = hasPermission(PERMISSIONS.CALENDAR.CREATE);
  const canUpdateCalendar = hasPermission(PERMISSIONS.CALENDAR.UPDATE);
  const canDeleteCalendar = hasPermission(PERMISSIONS.CALENDAR.DELETE);

  const calendarStatusTabs: IVerticalTabProps<ICalendarSet[]>[] = useMemo(
    () => [
      {
        label: "All",
        value: "All",
        filter: {},
      },
      {
        label: CalendarSetStatus.Active,
        value: CalendarSetStatus.Active,
        filter: {
          status: { eq: CalendarSetStatus.Active },
        },
      },
      {
        label: CalendarSetStatus.Inactive,
        value: CalendarSetStatus.Inactive,
        filter: {
          status: { eq: CalendarSetStatus.Inactive },
        },
      },
      {
        label: CalendarSetStatus.Draft,
        value: CalendarSetStatus.Draft,
        filter: {
          status: { eq: CalendarSetStatus.Draft },
        },
      },
    ],
    []
  );

  const {
    isLoading,
    calendarSets,
    recentCalendarSets,
    filter,
    refresh,
    setCalendarSets,
    setRecentCalendarSets,
    setFilter,
    getCalendarSets,
    duplicateCalendarSet,
    updateCalendarSetStatus,
    getRecentCalendarSets,
    deleteCalendarSet,
  } = useCalendarSets({
    module,
    eventType,
    debouncedSearch,
    includeDrafts: true,
  });

  const [triggerRefresh, setTriggerRefresh] = useState(false);

  const handleTabChange = (tab?: IVerticalTabProps<ICalendarSet[]> | null) => {
    setFilter(tab?.filter || {});
    setSearch("");
  };

  const refreshData = () => {
    setTriggerRefresh((prev) => !prev);
  };

  const navigateToCalendarDetails = (calendarId: string) => {
    navigate(`${pathname}/${calendarId}`);
  };

  const navigateToAddCalendar = () => {
    navigate(`${pathname}/add-calendar`);
  };

  const handleDeleteCalendarSet = async (calendarSet: ICalendarSet | null) => {
    if (calendarSet) {
      await deleteCalendarSet(calendarSet);
      setSelectedCalendarToDelete(null);
      refreshData();
    }
  };

  const handleDuplicateCalendarSet = (
    data: ICalendarSet,
    options: ICalendarSetDuplicateOptions
  ) => {
    duplicateCalendarSet(data, options);
    setSelectedCalendarToDuplicate(null);
    refreshData();
  };

  const refreshCalendars = useMemo(
    () => ({ filter, refresh, triggerRefresh, pathname }),
    [filter, refresh, triggerRefresh, pathname]
  );

  return (
    <ConfigPageLayout
      title={`${CALENDAR_PAGE_CONFIG[eventType].calendarName} Calendars`}
      loading={isLoading}
      titleActionComponent={
        canCreateCalendar &&
        pathname !== CONFIG_PATHS[CONFIG_PAGES.ROLES_OPERATIONS] && (
          <AddButton
            onClick={navigateToAddCalendar}
            label={`Add ${CALENDAR_PAGE_CONFIG[eventType].calendarName} Calendar`}
          />
        )
      }
      search={search}
      onHandleSearch={({ target: { value } }) => setSearch(value)}
      searchPlaceholder="Search"
      hideOnBack
    >
      <VerticalTabsLayout
        hideStatusIcons
        tabs={calendarStatusTabs}
        onTabChange={handleTabChange}
        hideNavigationButtons
      >
        <MiList
          viewType="card"
          data={calendarSets}
          dataProvider={getCalendarSets}
          loading={isLoading}
          setData={setCalendarSets}
          recentData={recentCalendarSets}
          setRecentData={setRecentCalendarSets}
          recentDataProvider={getRecentCalendarSets}
          CardComponent={ConfigCalendarSetCard}
          cardProps={{
            isLoading,
            canDelete: canDeleteCalendar,
            canUpdate: canUpdateCalendar,
            navigateToDetails: navigateToCalendarDetails,
            onDuplicate: setSelectedCalendarToDuplicate,
            onUpdateStatus: updateCalendarSetStatus,
            onClick: navigateToCalendarDetails,
            onDelete: setSelectedCalendarToDelete,
          }}
          paginationMode={PAGINATION.SERVER}
          refresh={refreshCalendars}
          entityName="Calendars"
          search={debouncedSearch}
          emptyListProps={{
            text: "No Order Calendars found",
          }}
        />
      </VerticalTabsLayout>
      {selectedCalendarToDelete && (
        <MiModalConfirm
          title={MODAL_TITLES.DELETE}
          textPositive={BUTTON_LABELS.DELETE}
          open={!!selectedCalendarToDelete}
          disableClick={isLoading}
          handleNegative={() => setSelectedCalendarToDelete(null)}
          handlePositive={() =>
            handleDeleteCalendarSet(selectedCalendarToDelete)
          }
          description={`You are about to delete the ${selectedCalendarToDelete?.name} calendar. Are you sure you would like to continue?`}
          buttonPositiveColor="error"
        />
      )}
      {selectedCalendarToDuplicate && (
        <ConfigDuplicateCalendarModal
          isLoading={isLoading}
          open={!!selectedCalendarToDuplicate}
          calendar={selectedCalendarToDuplicate}
          onConfirm={handleDuplicateCalendarSet}
          onClose={() => setSelectedCalendarToDuplicate(null)}
        />
      )}
    </ConfigPageLayout>
  );
};

interface ConfigCalendarSetListProps {
  module: PlatformModules;
  eventType: CalendarEventType;
}
